import {
  createBrowserHistory,
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
  Outlet,
  type RouterHistory,
} from '@tanstack/react-router'

/**
 * O enquadramento de uma tela, declarado pela própria rota — F085.
 *
 * `StaticDataRouteOption` é uma interface vazia que o TanStack Router expõe
 * exatamente para isto: cada aplicação declara o que quer carregar junto de
 * uma rota, com tipo. Ausente significa "página", que é o enquadramento de
 * vinte e duas das vinte e três telas.
 */
declare module '@tanstack/react-router' {
  // `interface`, e não `type`: só uma interface se FUNDE com a declaração da
  // biblioteca. Um `type` aqui seria um erro de "identificador duplicado", e é
  // o único lugar do repositório onde a regra do lint não se aplica.
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface StaticDataRouteOption {
    chrome?: 'workspace'
  }
}

import {
  RedirectWhenSignedIn,
  RequirePlatformAdmin,
} from './components/require-session.tsx'
import { ShellLayout } from './components/shell-layout.tsx'
import { validateReportSearch } from './lib/report-search.ts'
import { asWorkspaceModule, type WorkspaceModule } from './lib/workspace.ts'
import { AjudaPage } from './routes/ajuda.tsx'
import { AparenciaPage } from './routes/aparencia.tsx'
import { AplicacoesPage } from './routes/aplicacoes.tsx'
import { AssistentePage } from './routes/assistente.tsx'
import { AtendimentoCanaisPage } from './routes/atendimento-canais.tsx'
import {
  AtendimentoConfigHub,
  AtendimentoConfigLayout,
} from './routes/atendimento-config.tsx'
import { AtendimentoCustosPage } from './routes/atendimento-custos.tsx'
import { AtendimentoFilasPage } from './routes/atendimento-filas.tsx'
import { AtendimentoGruposPage } from './routes/atendimento-grupos.tsx'
import { AtendimentoGuiaPage } from './routes/atendimento-guia.tsx'
import { AtendimentoModelosNovoPage } from './routes/atendimento-modelos-novo.tsx'
import { AtendimentoModelosPage } from './routes/atendimento-modelos.tsx'
import { AtendimentoPainelPage } from './routes/atendimento-painel.tsx'
import { AtendimentoPosVendaPage } from './routes/atendimento-pos-venda.tsx'
import { AtendimentoRoboPrecoPage } from './routes/atendimento-robo-preco.tsx'
import { AtendimentoRoboPage } from './routes/atendimento-robo.tsx'
import { AtendimentoPage } from './routes/atendimento.tsx'
import { BaixarPage } from './routes/baixar.tsx'
import { BaterPontoPage } from './routes/bater-ponto.tsx'
import { CaixaPage } from './routes/caixa.tsx'
import { CanaisPage } from './routes/canais.tsx'
import { ChamadosDetalhePage } from './routes/chamados-detalhe.tsx'
import { ChamadosPage } from './routes/chamados.tsx'
import { CobrancasPage } from './routes/cobrancas.tsx'
import { ComissoesPage } from './routes/comissoes.tsx'
import { ComprasPage } from './routes/compras.tsx'
import {
  ConfiguracoesHub,
  ConfiguracoesLayout,
} from './routes/configuracoes.tsx'
import { CrmCampanhasNovaPage } from './routes/crm-campanhas-nova.tsx'
import { CrmCampanhasPage } from './routes/crm-campanhas.tsx'
import { CrmRegrasEditarPage } from './routes/crm-regras-editar.tsx'
import { CrmRegrasPage } from './routes/crm-regras.tsx'
import { EdgePage } from './routes/edge.tsx'
import { EmpresaPage } from './routes/empresa.tsx'
import { EntregasPage } from './routes/entregas.tsx'
import { EquipeProducaoPage } from './routes/equipe-producao.tsx'
import { EquipePage } from './routes/equipe.tsx'
import { EstoquePage } from './routes/estoque.tsx'
import { EtiquetasPage } from './routes/etiquetas.tsx'
import { FinanceiroPage } from './routes/financeiro.tsx'
import { FormasDePagamentoPage } from './routes/formas-de-pagamento.tsx'
import { GastosPage } from './routes/gastos.tsx'
import { ImportarClientesPage } from './routes/importar-clientes.tsx'
import { ImportarFotosRoute } from './routes/importar-fotos.tsx'
import { ImportarNfePage } from './routes/importar-nfe.tsx'
import { ImportarPage } from './routes/importar.tsx'
import { ImpressorasPage } from './routes/impressoras.tsx'
import { InicioPage } from './routes/inicio.tsx'
import { LoginPage } from './routes/login.tsx'
import { LojaOnlinePage } from './routes/loja-online.tsx'
import { NotaFiscalPage } from './routes/nota-fiscal.tsx'
import { NotasFiscaisPage } from './routes/notas-fiscais.tsx'
import { MarcadoresPage } from './routes/marcadores.tsx'
import { MarcasPage } from './routes/marcas.tsx'
import { MeuAssistentePage } from './routes/meu-assistente.tsx'
import { ModeloDeEtiquetaEditarPage } from './routes/modelo-de-etiqueta-editar.tsx'
import { ModeloDeEtiquetaPage } from './routes/modelo-de-etiqueta.tsx'
import { ModeloDeNotaPage } from './routes/modelo-de-nota.tsx'
import { OrcamentosPage } from './routes/orcamentos.tsx'
import { PagamentoPage } from './routes/pagamento.tsx'
import { PdvPage } from './routes/pdv.tsx'
import { PedidosCanalPage } from './routes/pedidos-canal.tsx'
import { PedidosLojaPage } from './routes/pedidos-loja.tsx'
import { PessoasPage } from './routes/pessoas.tsx'
import { PixPage } from './routes/pix.tsx'
import { PlataformaPage } from './routes/plataforma.tsx'
import { PontoPage } from './routes/ponto.tsx'
import { PrecosPage } from './routes/precos.tsx'
import { ProducaoPage } from './routes/producao.tsx'
import { ProdutosPage } from './routes/produtos.tsx'
import { RelatoriosPage } from './routes/relatorios.tsx'
import { RotasPage } from './routes/rotas.tsx'
import { ServidorPage } from './routes/servidor.tsx'
import { TerminaisPage } from './routes/terminais.tsx'
import { TrocarSenhaPage } from './routes/trocar-senha.tsx'
import { TrocasPage } from './routes/trocas.tsx'
import { VendasPage } from './routes/vendas.tsx'

/**
 * Routes declared in code rather than generated from the filesystem.
 *
 * A generated route tree is a build artifact that has to stay in sync and be
 * committed; with three routes it buys nothing. This is revisited when the
 * route count actually justifies it.
 */

const rootRoute = createRootRoute({ component: Outlet })

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: () => (
    <RedirectWhenSignedIn>
      <LoginPage />
    </RedirectWhenSignedIn>
  ),
})

/**
 * Outside the shell, deliberately.
 *
 * Somebody sent here is holding a password an administrator generated, and the
 * API refuses every permission-checked route until they change it. A sidebar
 * offering Produtos and Equipe would be an interface advertising screens that
 * cannot open. It is reachable voluntarily too, from the account menu.
 */
const trocarSenhaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/trocar-senha',
  component: TrocarSenhaPage,
})

/**
 * Fora do shell pelo mesmo motivo do login: quem troca de servidor pode estar
 * apontando para um que não responde, e um menu cheio de módulos que não abrem
 * seria uma promessa falsa. Não exige sessão — se o servidor está errado, não
 * há como ter uma.
 */
/**
 * O link de pagamento do Pix (F207). Fora do shell e sem sessão: quem abre é
 * o CLIENTE da loja, pelo WhatsApp.
 */
const pagamentoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pagamento/$token',
  component: PagamentoPage,
})

/**
 * A página que bate o ponto (F339). Fora do shell e sem sessão: quem abre é
 * o tablet da loja ou o celular do funcionário, pareados por um link do
 * gerente — a credencial é a do aparelho, não a de uma pessoa.
 */
const baterPontoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/bater-ponto',
  component: BaterPontoPage,
})

const servidorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/servidor',
  component: ServidorPage,
})

/**
 * Shell layout for everything inside the application.
 *
 * The guard is a component rather than a `beforeLoad`, because the session is
 * resolved by the server and cached in React Query. It is navigation, not
 * access control: the API refuses unauthenticated requests independently, so
 * defeating this in a browser console reveals an empty shell and nothing else.
 */
const shellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'shell',
  component: ShellLayout,
})

/**
 * As duas páginas de configuração (F068).
 *
 * Rotas de layout **sem `path`** — o mesmo recurso que `shellRoute` usa. As
 * catorze telas abaixo continuam nos endereços que sempre tiveram
 * (`/atendimento-canais`, `/empresa`); o que elas ganham é uma moldura com a
 * trilha de abas em volta.
 *
 * Mover as URLs para `/configuracoes/empresa` teria sido o caminho óbvio e
 * caro: `page-access.test.ts` exige `moduleForPath('/${module}') === module`
 * para toda entrada de `PAGES`, e `PAGES` é o que dá ao administrador a
 * permissão POR TELA. O motivo longo está em `routes/atendimento-config.tsx`.
 */
const atendimentoConfigLayoutRoute = createRoute({
  getParentRoute: () => shellRoute,
  id: 'atendimento-config-layout',
  component: AtendimentoConfigLayout,
})

const configuracoesLayoutRoute = createRoute({
  getParentRoute: () => shellRoute,
  id: 'configuracoes-layout',
  component: ConfiguracoesLayout,
})

/*
 * Os hubs são a LINHA DO MENU, e ficam FORA do layout de propósito: eles não
 * desenham conteúdo, mandam para a primeira aba que a pessoa pode abrir.
 * Dentro do layout, o cabeçalho e a trilha piscariam antes do redirecionamento.
 */
const atendimentoConfigRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/atendimento-config',
  component: AtendimentoConfigHub,
})

const configuracoesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/configuracoes',
  component: ConfiguracoesHub,
})

/**
 * As máquinas da loja (F041).
 *
 * Rota própria e não uma aba de Terminais: um terminal é uma gaveta de
 * dinheiro, e uma máquina é um computador que sincroniza. Quem procura "o PC
 * do balcão parou de mandar as vendas" não abre a tela dos caixas.
 */
const edgeRoute = createRoute({
  getParentRoute: () => configuracoesLayoutRoute,
  path: '/edge',
  component: EdgePage,
})

/**
 * O instalador do Windows (F043).
 *
 * Dentro do shell e sem permissão: quem tem conta pode instalar o produto na
 * própria máquina, e exigir um papel para isso só produziria um pedido ao
 * administrador.
 */
const baixarRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/baixar',
  component: BaixarPage,
})

const indexRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/',
  beforeLoad: () => {
    // TanStack Router signals a redirect by throwing its own marker object
    // rather than an Error; this is the framework's documented contract.
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: '/inicio' })
  },
})

const inicioRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/inicio',
  component: InicioPage,
})

/*
 * O glossário (F156). Sem `PageGate`: está em `UNDENIABLE_PAGES`, e quem tem
 * menos telas é quem mais precisa da explicação.
 */
const ajudaRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/ajuda',
  component: AjudaPage,
})

const produtosRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/produtos',
  component: ProdutosPage,
})

const trocasRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/trocas',
  component: TrocasPage,
})

const etiquetasRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/etiquetas',
  component: EtiquetasPage,
})

const importarRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/importar',
  component: ImportarPage,
})

const estoqueRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/estoque',
  component: EstoquePage,
})

const caixaRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/caixa',
  component: CaixaPage,
})

/**
 * O PDV.
 *
 * Its own route rather than a mode of /vendas: section 18 is explicit that the
 * till is a specific experience and "não deve ser apenas formulário padrão".
 * Same domain underneath, different screen on top.
 */
const pdvRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/pdv',
  component: PdvPage,
})

/**
 * Os orçamentos abertos, do lado do caixa.
 *
 * Rota própria e não uma aba de Vendas: quem abre esta tela tem um cliente na
 * frente com um papel na mão, e a busca precisa estar focada quando ela abre.
 */
const orcamentosRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/orcamentos',
  component: OrcamentosPage,
})

/**
 * O acerto do motoboy.
 *
 * Rota própria e não uma aba de Financeiro: a pergunta desta tela começa por
 * uma pessoa — quem voltou da rua — e não por um título. Quem abre Financeiro
 * está procurando uma dívida; quem abre esta tem um entregador na frente com
 * um maço de comprovantes.
 */
const entregasRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/entregas',
  component: EntregasPage,
})

/* As rotas de entrega (F265–F267), atrás da chave `entregas-rota`. */
const rotasRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/rotas',
  component: RotasPage,
})

/* As notas (F372), atrás da chave `fiscal`. */
const notasFiscaisRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/notas-fiscais',
  component: NotasFiscaisPage,
})

/* O ponto dos funcionários (F339), atrás da chave `ponto`. */
const pontoRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/ponto',
  component: PontoPage,
})

/*
 * A fila de produção de uniformes (F170), atrás da chave `uniformes`.
 *
 * `?pedido=<id>` abre a ficha daquele pedido — é o que a janela do Modo PC e um
 * link colado usam. Validado como toda entrada do usuário: vazio ou não-texto
 * vira ausência, e a fila abre normal.
 */
const producaoRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/producao',
  component: ProducaoPage,
  validateSearch: (search: Record<string, unknown>): { pedido?: string } =>
    typeof search.pedido === 'string' && search.pedido !== ''
      ? { pedido: search.pedido }
      : {},
})

const precosRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/precos',
  component: PrecosPage,
})

/**
 * The payment-method catalogue.
 *
 * A settings screen rather than a business one — which is why it sits behind
 * `payment.method.manage` and not behind `payment.write`.
 */
const marcadoresRoute = createRoute({
  getParentRoute: () => configuracoesLayoutRoute,
  path: '/marcadores',
  component: MarcadoresPage,
})

const marcasRoute = createRoute({
  getParentRoute: () => configuracoesLayoutRoute,
  path: '/marcas',
  component: MarcasPage,
})

const modeloDeEtiquetaRoute = createRoute({
  getParentRoute: () => configuracoesLayoutRoute,
  path: '/modelo-de-etiqueta',
  component: ModeloDeEtiquetaPage,
})

/* O modelo da nota térmica (F149): formulário e prévia, dentro da aba. */
const modeloDeNotaRoute = createRoute({
  getParentRoute: () => configuracoesLayoutRoute,
  path: '/modelo-de-nota',
  component: ModeloDeNotaPage,
})

/*
 * A marca da loja (F169), atrás da chave `marca-propria`. Aba de
 * Configurações, como o modelo da nota: é configuração da loja, não um espaço
 * de trabalho.
 */
const aparenciaRoute = createRoute({
  getParentRoute: () => configuracoesLayoutRoute,
  path: '/aparencia',
  component: AparenciaPage,
})

/* A loja online (F221): aba de Configurações, atrás da chave `loja-online`. */
const lojaOnlineRoute = createRoute({
  getParentRoute: () => configuracoesLayoutRoute,
  path: '/loja-online',
  component: LojaOnlinePage,
})

/* A nota fiscal (F371): aba de Configurações, atrás da chave `fiscal`. */
const notaFiscalRoute = createRoute({
  getParentRoute: () => configuracoesLayoutRoute,
  path: '/nota-fiscal',
  component: NotaFiscalPage,
})

/* O assistente (F253): aba de Configurações, atrás da chave `assistente`. */
const assistenteRoute = createRoute({
  getParentRoute: () => configuracoesLayoutRoute,
  path: '/assistente',
  component: AssistentePage,
})

/* Meu assistente (F253): o Claude (e, na F254, o WhatsApp) da pessoa. */
const meuAssistenteRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/meu-assistente',
  component: MeuAssistentePage,
})

/* As impressoras da produção (F170): aba de Configurações, atrás da chave. */
const impressorasRoute = createRoute({
  getParentRoute: () => configuracoesLayoutRoute,
  path: '/impressoras',
  component: ImpressorasPage,
})

/* A função de cada pessoa na produção (F171): aba de Configurações, atrás da chave. */
const equipeProducaoRoute = createRoute({
  getParentRoute: () => configuracoesLayoutRoute,
  path: '/equipe-producao',
  component: EquipeProducaoPage,
})

/*
 * O editor de um modelo (F129). `novo` no lugar do id cria um. É um ESPAÇO DE
 * TRABALHO, como o atendimento: lista de campos, prévia grande e painel de
 * propriedades não cabem dentro da aba de configuração.
 */
const modeloDeEtiquetaEditarRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/modelo-de-etiqueta/$id',
  component: ModeloDeEtiquetaEditarPage,
  staticData: { chrome: 'workspace' },
})

const formasDePagamentoRoute = createRoute({
  getParentRoute: () => configuracoesLayoutRoute,
  path: '/formas-de-pagamento',
  component: FormasDePagamentoPage,
})

/**
 * The till register.
 *
 * A settings screen: which drawers exist in a shop. Separate from /caixa,
 * which is the shift running on one of them.
 */
const terminaisRoute = createRoute({
  getParentRoute: () => configuracoesLayoutRoute,
  path: '/terminais',
  component: TerminaisPage,
})

/**
 * Sales channels: the marketplaces a shop lists on, and the mapping between an
 * Aeris product and an advert. Configuration, not an operational screen.
 */
/**
 * The application register: what a part fits. Vocabulary, not operation — the
 * compatibility itself is attached on the product's own screen.
 */
const aplicacoesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/aplicacoes',
  component: AplicacoesPage,
})

const canaisRoute = createRoute({
  getParentRoute: () => configuracoesLayoutRoute,
  path: '/canais',
  component: CanaisPage,
})

/**
 * A conta de WhatsApp que fala com o gateway (OpenBSP), F045.
 *
 * `/atendimento-canais` e não `/canais`: aquela rota já é o marketplace
 * (F030/F032). Mesma palavra, assunto diferente — o menu já as separa por
 * grupo e permissão, e a URL não devia fingir que são a mesma coisa.
 *
 * Achatada — `/atendimento-canais`, não `/atendimento/canais` — como
 * `importar-clientes` e `pedidos-canal`: `moduleForPath` espera `/${module}`
 * de volta para `module` (page-access.test.ts), e uma rota aninhada quebraria
 * essa garantia. As telas seguintes do módulo seguem o mesmo padrão achatado.
 */
const atendimentoCanaisRoute = createRoute({
  getParentRoute: () => atendimentoConfigLayoutRoute,
  path: '/atendimento-canais',
  component: AtendimentoCanaisPage,
})

/**
 * O inbox de WhatsApp (F046): a lista de conversas e a conversa aberta, na
 * mesma tela. `/atendimento`, achatada como as demais — ver o comentário em
 * `lib/workspace.ts`.
 */
/**
 * A única tela do produto que é um ESPAÇO DE TRABALHO e não uma página (F085).
 *
 * O `chrome` é lido pelo `AppShell`, que tira o padding do `<main>` e troca a
 * rolagem por `overflow-hidden`. A declaração mora aqui, na rota, e não numa
 * comparação de `pathname` dentro do shell: a moldura é uma propriedade da
 * TELA, e o shell não deveria guardar uma lista de endereços especiais.
 */
const atendimentoRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/atendimento',
  component: AtendimentoPage,
  staticData: { chrome: 'workspace' },
})

/** Os grupos do WhatsApp que entram no Atendimento (F268). */
const atendimentoGruposRoute = createRoute({
  getParentRoute: () => atendimentoConfigLayoutRoute,
  path: '/atendimento-grupos',
  component: AtendimentoGruposPage,
})

/**
 * Filas de atendimento, membros e horário (F047). Achatada — `/atendimento-filas`
 * — pelo mesmo motivo de `/atendimento-canais` acima.
 */
const atendimentoFilasRoute = createRoute({
  getParentRoute: () => atendimentoConfigLayoutRoute,
  path: '/atendimento-filas',
  component: AtendimentoFilasPage,
})

/**
 * Modelos aprovados pela Meta, e a janela de 24h (F050). Achatada —
 * `/atendimento-modelos` — pelo mesmo motivo de `/atendimento-canais` acima.
 */
const atendimentoModelosRoute = createRoute({
  getParentRoute: () => atendimentoConfigLayoutRoute,
  path: '/atendimento-modelos',
  component: AtendimentoModelosPage,
})

/**
 * A criação de um modelo, como PÁGINA e não modal (F059) — formulário à
 * esquerda, pré-visualização fiel do WhatsApp à direita. A ÚNICA sub-rota
 * deste catálogo: `moduleForPath` (`lib/workspace.ts`) resolve
 * `/atendimento-modelos/novo` para o módulo `atendimento-modelos` pelo
 * prefixo mais longo, então `page-access.test.ts` continua valendo sem
 * precisar de um módulo novo.
 */
const atendimentoModelosNovoRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/atendimento-modelos/novo',
  component: AtendimentoModelosNovoPage,
})

/**
 * O robô de IA (F052). Achatada — `/atendimento-robo` — pelo mesmo motivo
 * de `/atendimento-canais` acima.
 */
const atendimentoRoboRoute = createRoute({
  getParentRoute: () => atendimentoConfigLayoutRoute,
  path: '/atendimento-robo',
  component: AtendimentoRoboPage,
})

/** O robô de preço (F193): a tabela que um número responde sozinho. */
const atendimentoRoboPrecoRoute = createRoute({
  getParentRoute: () => atendimentoConfigLayoutRoute,
  path: '/atendimento-robo-preco',
  component: AtendimentoRoboPrecoPage,
})

/** O "saiu para entrega" e o pós-venda (F245/F246). */
const atendimentoPosVendaRoute = createRoute({
  getParentRoute: () => atendimentoConfigLayoutRoute,
  path: '/atendimento-pos-venda',
  component: AtendimentoPosVendaPage,
})

/**
 * Chamados (F051): protocolo, situação, prioridade, responsável e o
 * histórico de cada um — NÃO ordem de serviço técnica. `/chamados`, achatada
 * como as demais telas do módulo.
 */
const chamadosRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/chamados',
  component: ChamadosPage,
})

/**
 * UM chamado (F061). Era um diálogo sem URL: não dava para mandar um chamado
 * para um colega por link, embora o protocolo exista desde a F051 justamente
 * para ser citado.
 */
const chamadosDetalheRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/chamados/$id',
  component: ChamadosDetalhePage,
})

/**
 * Follow-up (F053): "comprou ar, lembrar da manutenção em 6 meses".
 * `/crm-regras`, achatada como as demais telas do módulo.
 */
const crmRegrasRoute = createRoute({
  getParentRoute: () => atendimentoConfigLayoutRoute,
  path: '/crm-regras',
  component: CrmRegrasPage,
})

/**
 * Criar e editar uma regra de follow-up como PÁGINA (F065) — a regra passou a
 * aceitar vários produtos, e uma lista que cresce dentro de um diálogo de
 * altura fixa empurra os botões para o meio da caixa. Duas sub-rotas do mesmo
 * componente: `nova` sem id, `$id` para editar.
 */
const crmRegrasNovaRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/crm-regras/nova',
  component: CrmRegrasEditarPage,
})

const crmRegrasEditarRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/crm-regras/$id',
  component: CrmRegrasEditarPage,
})

/**
 * Campanhas (F054): "pegar todos os clientes que compraram X e mandar
 * mensagem de reajuste". `/crm-campanhas`, ao lado de `/crm-regras`.
 */
const crmCampanhasRoute = createRoute({
  getParentRoute: () => atendimentoConfigLayoutRoute,
  path: '/crm-campanhas',
  component: CrmCampanhasPage,
})

/**
 * A criação de uma campanha como ASSISTENTE em página (F060). Sub-rota, pelo
 * mesmo motivo de `/atendimento-modelos/novo`: `moduleForPath`
 * (`lib/workspace.ts`) resolve pelo prefixo mais longo, então
 * `page-access.test.ts` continua valendo sem um módulo novo.
 */
const crmCampanhasNovaRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/crm-campanhas/nova',
  component: CrmCampanhasNovaPage,
})

/** Custos de atendimento (F054): a conta no fim do mês. */
const atendimentoCustosRoute = createRoute({
  getParentRoute: () => atendimentoConfigLayoutRoute,
  path: '/atendimento-custos',
  component: AtendimentoCustosPage,
})

/**
 * O guia de configuração (F055): os dez passos do módulo de atendimento,
 * derivados do banco. `/atendimento-guia`, achatada como as demais telas do
 * módulo.
 */
const atendimentoGuiaRoute = createRoute({
  getParentRoute: () => atendimentoConfigLayoutRoute,
  path: '/atendimento-guia',
  component: AtendimentoGuiaPage,
})

/** The marketplace inbox: what a channel sold, and what became a sale. */
const pedidosCanalRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/pedidos-canal',
  component: PedidosCanalPage,
})

/** O pedido da loja online, para a vendedora aprovar (F224). */
const pedidosLojaRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/pedidos-loja',
  component: PedidosLojaPage,
})

const comprasRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/compras',
  component: ComprasPage,
})

/**
 * One route for both directions.
 *
 * Contas a receber and contas a pagar are the same object seen from opposite
 * sides, switched inside the screen — two routes would be one component copied
 * with a word changed.
 */
const financeiroRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/financeiro',
  component: FinanceiroPage,
})

/**
 * Cobranças (F135). `?cliente=<id>` abre direto o painel de um cliente — o
 * destino do "quem mais deve" do Início e do "Cobrar" do Financeiro. Validado
 * como toda entrada do usuário: vazio ou não-texto vira ausência.
 */
const cobrancasRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/cobrancas',
  component: CobrancasPage,
  validateSearch: (search: Record<string, unknown>): { cliente?: string } =>
    typeof search.cliente === 'string' && search.cliente !== ''
      ? { cliente: search.cliente }
      : {},
})

/**
 * Comissões (F137): a comissão de cada vendedor no mês, fechar e desfazer.
 * `?mes=AAAA-MM` abre direto um mês — validado como toda entrada do usuário.
 */
const comissoesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/comissoes',
  component: ComissoesPage,
  validateSearch: (search: Record<string, unknown>): { mes?: string } =>
    typeof search.mes === 'string' && /^\d{4}-(0[1-9]|1[0-2])$/.test(search.mes)
      ? { mes: search.mes }
      : {},
})

/**
 * Relatórios (F158): os rankings com período, corte, "Outros" e total.
 *
 * A ÚNICA tela do produto que guarda os filtros na URL, e é uma exceção com
 * motivo: um relatório se manda por link, sobrevive ao recarregar, e a janela
 * do Modo PC o restaura. A validação é a mesma disciplina das outras — o que
 * vem na URL é entrada do usuário, e o que não é exatamente um valor conhecido
 * vira ausência.
 */
const relatoriosRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/relatorios',
  component: RelatoriosPage,
  validateSearch: validateReportSearch,
})

/** O painel do atendimento (F210): a carga de cada um, e a distribuição. */
const atendimentoPainelRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/atendimento-painel',
  component: AtendimentoPainelPage,
})

/** Pix recebidos (F205): quanto entrou em Pix pelo atendimento. */
const pixRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/pix',
  component: PixPage,
})

/** Folha e gastos (F146): a folha dos funcionários e o caderno de gastos. */
const gastosRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/gastos',
  component: GastosPage,
})

/**
 * Trazer a carteira de clientes do sistema antigo.
 *
 * Separada de /importar, que traz o catálogo, porque o passo do meio é
 * diferente: aqui a pergunta é o que significa cada marca que alguém deixou no
 * nome, e são regras que valem para centenas de linhas de uma vez.
 */
const importarClientesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/importar-clientes',
  component: ImportarClientesPage,
})

/**
 * As fotos do catálogo, separadas da planilha que traz os produtos.
 *
 * São dois arquivos que a loja produz em momentos diferentes — a planilha sai
 * do sistema antigo, as fotos saem de uma pasta —, e juntá-los numa tela só
 * obrigaria a esperar os dois para poder mandar qualquer um.
 */
const importarFotosRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/importar-fotos',
  component: ImportarFotosRoute,
})

/**
 * Its own route rather than a dialog inside Compras.
 *
 * The review step is a table with a decision per line, and a dialog that has to
 * be scrolled to answer forty questions is a dialog people answer badly.
 */
const importarNfeRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/importar-nfe',
  component: ImportarNfePage,
})

const empresaRoute = createRoute({
  getParentRoute: () => configuracoesLayoutRoute,
  path: '/empresa',
  component: EmpresaPage,
})

const vendasRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/vendas',
  component: VendasPage,
})

const equipeRoute = createRoute({
  getParentRoute: () => configuracoesLayoutRoute,
  path: '/equipe',
  component: EquipePage,
})

/**
 * O console da plataforma (F119).
 *
 * Filha de `shellRoute` — dentro do shell, porque quem administra a instalação
 * também é uma pessoa com sessão —, e **fora** de `MODULES`, `PAGES` e
 * `MODULE_ROUTES`. Uma entrada em `PAGES` sem permissão seria tratada como
 * `inicio` e apareceria no menu de todo caixa de toda loja; uma com permissão
 * exigiria inventar uma permissão de loja para uma tela que não é de loja. O
 * precedente é `/trocar-senha` e `/servidor`, e `page-access.test.ts` continua
 * satisfeito porque as listas de módulos não mudaram.
 *
 * `moduleForPath('/plataforma')` devolve `null`, e o `PageGate` deixa passar
 * rota fora do mapa por decisão já escrita lá. Quem recusa aqui é o
 * `RequirePlatformAdmin`; quem recusa de verdade é a API.
 */
const plataformaRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/plataforma',
  component: () => (
    <RequirePlatformAdmin>
      <PlataformaPage />
    </RequirePlatformAdmin>
  ),
})

const pessoasRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/pessoas',
  component: PessoasPage,
  /**
   * `?editar=<id>` — a primeira rota do produto com parâmetro de busca (F087).
   *
   * Existe porque o inbox precisa de um destino: o marcador `+ Cidade` do
   * cabeçalho da conversa leva ao cadastro DAQUELA pessoa, e sem parâmetro o
   * atendente cairia na lista de Pessoas para procurar de novo alguém que a
   * tela ao lado já sabia quem era.
   *
   * Validar em vez de confiar: o que vem na URL é entrada do usuário como
   * qualquer outra. Um `?editar=` vazio, repetido ou com um objeto dentro vira
   * ausência, e a página abre normal.
   */
  validateSearch: (
    search: Record<string, unknown>
  ): { editar?: string; voltar?: WorkspaceModule } => {
    /*
     * `voltar` NUNCA vira caminho direto (F089): ele é a CHAVE de um mapa que
     * `workspace.ts` declara, e qualquer coisa fora dele vira ausência. É o que
     * torna um redirecionamento aberto impossível a partir desta URL.
     */
    const voltar = asWorkspaceModule(search.voltar)
    return {
      ...(typeof search.editar === 'string' && search.editar !== ''
        ? { editar: search.editar }
        : {}),
      ...(voltar === null ? {} : { voltar }),
    }
  },
})

const routeTree = rootRoute.addChildren([
  loginRoute,
  trocarSenhaRoute,
  servidorRoute,
  pagamentoRoute,
  baterPontoRoute,
  shellRoute.addChildren([
    indexRoute,
    inicioRoute,
    ajudaRoute,
    plataformaRoute,
    produtosRoute,
    etiquetasRoute,
    trocasRoute,
    importarRoute,
    pessoasRoute,
    estoqueRoute,
    precosRoute,
    caixaRoute,
    pdvRoute,
    atendimentoModelosNovoRoute,
    crmCampanhasNovaRoute,
    crmRegrasNovaRoute,
    crmRegrasEditarRoute,
    atendimentoRoute,
    modeloDeEtiquetaEditarRoute,
    chamadosRoute,
    chamadosDetalheRoute,
    pedidosCanalRoute,
    pedidosLojaRoute,
    aplicacoesRoute,
    vendasRoute,
    comprasRoute,
    financeiroRoute,
    cobrancasRoute,
    comissoesRoute,
    relatoriosRoute,
    gastosRoute,
    pixRoute,
    atendimentoPainelRoute,
    orcamentosRoute,
    entregasRoute,
    rotasRoute,
    pontoRoute,
    notasFiscaisRoute,
    producaoRoute,
    importarClientesRoute,
    importarFotosRoute,
    importarNfeRoute,
    baixarRoute,
    meuAssistenteRoute,
    atendimentoConfigRoute,
    configuracoesRoute,
    atendimentoConfigLayoutRoute.addChildren([
      atendimentoGuiaRoute,
      atendimentoCanaisRoute,
      atendimentoGruposRoute,
      atendimentoFilasRoute,
      atendimentoModelosRoute,
      atendimentoRoboRoute,
      atendimentoRoboPrecoRoute,
      atendimentoPosVendaRoute,
      crmRegrasRoute,
      crmCampanhasRoute,
      atendimentoCustosRoute,
    ]),
    configuracoesLayoutRoute.addChildren([
      marcadoresRoute,
      marcasRoute,
      modeloDeEtiquetaRoute,
      modeloDeNotaRoute,
      aparenciaRoute,
      lojaOnlineRoute,
      notaFiscalRoute,
      assistenteRoute,
      impressorasRoute,
      equipeProducaoRoute,
      formasDePagamentoRoute,
      terminaisRoute,
      canaisRoute,
      empresaRoute,
      equipeRoute,
      edgeRoute,
    ]),
  ]),
])

/**
 * Um roteador sobre a árvore de rotas do Aeris — F138.
 *
 * Uma fábrica, e não um roteador de módulo: no Modo PC cada janela interna é
 * uma tela com a navegação DELA (um "voltar" que não mexe na janela ao lado), e
 * isso é um roteador por janela, todos sobre a mesma árvore. A árvore não
 * guarda estado de roteador nenhum, então compartilhá-la é seguro.
 *
 * - `history`: a navegação desta árvore. Ausente, a barra de endereço — que é
 *   o que a página principal usa.
 * - `preload`: pré-carregar ao passar o mouse. As janelas não precisam, e numa
 *   máquina de balcão é trabalho a menos a cada movimento do mouse.
 */
export const createAppRouter = ({
  history,
  preload = true,
}: {
  history?: RouterHistory
  preload?: boolean
} = {}) =>
  createRouter({
    routeTree,
    history: history ?? createBrowserHistory(),
    defaultPreload: preload ? 'intent' : false,
    // Branch machines are not fast; a short delay avoids fetching on every
    // incidental hover.
    defaultPreloadDelay: 150,
  })

export type AppRouter = ReturnType<typeof createAppRouter>

declare module '@tanstack/react-router' {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Register {
    router: AppRouter
  }
}
